import { LeagueRegistration, LeagueTeamMemberRegistration } from "@/models";
import { ActionTree } from "vuex";
import { SportEventState } from ".";
import eventsApi from "../../api/events.api";
import leagueApi from "../../api/league.api";
import roundsApi from "../../api/rounds.api";
import teamsApi from "../../api/teams.api";
import i18n from "../../i18n";
import { ToastMessage } from "../../models/toast-message.model";
import { RootState } from "./../index";
import { RootActionTypes } from "./../root-actions";
import { RootMutationTypes } from "./../root-mutations";
import { SportEventMutationTypes } from "./sport-event.mutations";
import * as Sentry from "@/utils/logger.util";

export const SportEventActionTypes = {
  FetchSportEvent: "FETCH_SPORT_EVENT",
  FetchLeagues: "FETCH_LEAGUES",
  FetchLeagueTeams: "FETCH_LEAGUE_TEAMS",
  FetchLeagueTable: "FETCH_LEAGUE_TABLE",
  FetchLeagueTopScorers: "FETCH_LEAGUE_TOP_SCORERS",
  FetchLeagueRounds: "FETCH_LEAGUE_ROUNDS",
  FetchLeagueRoundsWithMatches: "FETCH_LEAGUE_ROUNDS_WITH_MATCHES",
  FetchRoundMatches: "FETCH_ROUND_MATCHES",
  FetchUserTeams: "FETCH_USER_TEAMS",
  FetchUserTeamMembers: "FETCH_USER_TEAM_MEMBERS",
  RegisterTeam: "REGISTER_TEAM",
  RegisterTeamMembers: "REGISTER_TEAM_MEMBERS",
  UnregisterTeam: "UNREGISTER_TEAM",
  FetchRegisteredTeamMembers: "FETCH_REGISTERED_TEAM_MEMBERS"
};

export const actions: ActionTree<SportEventState, RootState> = {
  async [SportEventActionTypes.FetchSportEvent]({ commit, dispatch }, id: number) {
    commit(SportEventMutationTypes.ClearState);

    try {
      const response = await eventsApi.getEvent(id);
      commit(SportEventMutationTypes.SetSportEvent, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchLeagues]({ commit, dispatch }, id: number) {
    try {
      const response = await eventsApi.getEventLeagues(id);
      commit(SportEventMutationTypes.SetLeagues, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchLeagueTeams]({ commit, dispatch }, params: { leagueId: number }) {
    try {
      const response = await leagueApi.getLeagueTeams(params.leagueId);
      commit(SportEventMutationTypes.SetLeagueTeams, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchRegisteredTeamMembers]({ commit, dispatch }, params: { leagueId: number; teamId: number }) {
    try {
      const response = await leagueApi.getLeagueTeamMembers(params.leagueId, params.teamId);
      commit(SportEventMutationTypes.SetRegisteredTeamMembers, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchLeagueTable]({ commit, dispatch }, params: { leagueId: number }) {
    try {
      const response = await leagueApi.getLeagueTable(params.leagueId);
      commit(SportEventMutationTypes.SetLeagueTable, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchLeagueTopScorers]({ commit, dispatch }, params: { leagueId: number }) {
    try {
      const response = await leagueApi.getLeagueTopScorers(params.leagueId);
      commit(SportEventMutationTypes.SetTopScorers, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchLeagueRounds]({ commit, dispatch }, params: { leagueId: number }) {
    try {
      const response = await leagueApi.getLeagueRounds(params.leagueId);
      commit(SportEventMutationTypes.SetRounds, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchLeagueRoundsWithMatches]({ commit, dispatch }, params: { leagueId: number }) {
    try {
      const response = await leagueApi.getLeagueRoundsWithMatches(params.leagueId);
      commit(SportEventMutationTypes.SetRounds, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchRoundMatches]({ commit, dispatch }, params: { leagueId: number; roundId: number }) {
    try {
      const response = await roundsApi.getRoundMatches(params.roundId);
      commit(SportEventMutationTypes.SetRoundMatches, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchUserTeams]({ commit, dispatch }) {
    try {
      const response = await teamsApi.getTeams();
      commit(SportEventMutationTypes.SetUserTeams, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.FetchUserTeamMembers]({ commit, dispatch }, teamId: number) {
    try {
      const response = await teamsApi.getTeamMembers(teamId);
      commit(SportEventMutationTypes.SetUserTeamMembers, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.RegisterTeam](
    { commit, dispatch, state },
    params: { leagueId: number; leagueRegistration: LeagueRegistration }
  ) {
    try {
      const _ = await leagueApi.postLeagueRegistration(params.leagueId, params.leagueRegistration);
      const team = state.userTeams.find(team => team.id === params.leagueRegistration.team_id);
      commit(
        RootMutationTypes.SetToastMessages,
        {
          title: i18n.tc("alert.title.success"),
          variant: "success",
          content: i18n.t("events.team_registration_success", [team.name]).toString()
        } as ToastMessage,
        { root: true }
      );

      dispatch(SportEventActionTypes.FetchLeagueTeams, { leagueId: state.selectedLeagueId });
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [SportEventActionTypes.RegisterTeamMembers](
    { commit, dispatch, state },
    params: { leagueId: number; leagueTeamMemberRegistration: LeagueTeamMemberRegistration }
  ) {
    const team = state.teams.find(team => team.id === params.leagueTeamMemberRegistration.team_id);
    commit(SportEventMutationTypes.UpdateTeam, {
      teamId: team.id,
      value: { ...team, isLoading: true }
    });
    try {
      await leagueApi.putTeamMembers(params.leagueId, params.leagueTeamMemberRegistration);
      dispatch(SportEventActionTypes.FetchLeagueTeams, { leagueId: params.leagueId });
      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("events.register_team_members_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    } finally {
      commit(SportEventMutationTypes.UpdateTeam, {
        teamId: team.id,
        value: { ...team, isLoading: false }
      });
    }
  },
  async [SportEventActionTypes.UnregisterTeam]({ commit, dispatch, state }, params: { leagueId: number; teamId: number }) {
    const team = state.teams.find(team => team.id === params.teamId);
    commit(SportEventMutationTypes.UpdateTeam, {
      teamId: team.id,
      value: { ...team, isLoading: true }
    });

    try {
      await leagueApi.deleteRegistration(params.leagueId, params.teamId);
      await dispatch(SportEventActionTypes.FetchLeagueTeams, { leagueId: params.leagueId });

      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("events.unregister_team_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    } finally {
      commit(SportEventMutationTypes.UpdateTeam, {
        teamId: team.id,
        value: { ...team, isLoading: false }
      });
    }
  }
};
